import colors from 'tailwindcss/colors.js';
import {arrayEquals, hasElements} from '#lib/utils/array.js';
import {omit, pluck} from '#lib/utils/object.js';

export const legacyColors = ['lightBlue', 'warmGray', 'trueGray', 'coolGray', 'blueGray'];
export const neutralColors = ['slate', 'gray', 'zinc', 'neutral', 'stone'];
export const specialColors = ['inherit', 'current'];
export const alphaColors = ['black', 'white', 'transparent'];
export const defaultShades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

for (const deprecated of legacyColors) {
    delete colors[deprecated];
}
export const palettes = omit(colors, [...specialColors, ...alphaColors]);

export const neutralPalettes = pluck(colors, neutralColors);

export const shades = Object.keys(colors.gray);

/**
 * Given size tokens, it filters and replaces rem-defined tokens into em tokens
 * @see https://tailwindcss.com/docs/width
 * @param {Object} sizeTokens Tailwind size tokens (width, height, etc.)
 * @returns {Array} Filtered entry list with em sizes instead of rem ones
 */
function replaceRootFontSizeTokens(sizeTokens) {
    const filtered = Object.entries(sizeTokens).filter(([, value]) =>
        value.endsWith('em')
    );
    const relative = filtered.map(([key, value]) => [key, value.replace('rem', 'em')]);
    return relative;
}

/**
 * Adds parent element font size tokens (em) alongside
 * root element (rem) ones
 * @see https://tailwindcss.com/docs/plugins#adding-utilities
 */
export function addRelativeFontSizesPlugin({addUtilities, theme}) {
    let utilities = {};
    const widthTokens = theme('width');
    for (const [token, value] of replaceRootFontSizeTokens(widthTokens)) {
        const escaped = token.replace('.', '\\.');
        utilities[`.em-w-${escaped}`] = {width: value};
    }
    const heightTokens = theme('height');
    for (const [token, value] of replaceRootFontSizeTokens(heightTokens)) {
        const escaped = token.replace('.', '\\.');
        utilities[`.em-h-${escaped}`] = {height: value};
    }
    addUtilities(utilities);
}

/**
 * Grid template columns with `min-content` as `minmax()` minimum size
 */
export function addGridTemplateColsWithMinWidthPlugin({addUtilities, theme}) {
    let utilities = {};
    const tokens = theme('gridTemplateColumns');
    for (const token of Object.keys(tokens)) {
        if (token === 'none') continue;
        utilities[`.grid-mw-cols-${token}`] = {
            gridTemplateColumns: `repeat(${token}, minmax(min-content, 1fr))`
        };
    }
    addUtilities(utilities);
}

function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return null;
    const red = parseInt(result[1], 16);
    const green = parseInt(result[2], 16);
    const blue = parseInt(result[3], 16);
    return [red, green, blue];
}

/**
 * Inserts Tailwind color palettes as CSS custom properties
 * at the document level.
 */
export function addColorsToBaseAsCSSProps(namedColors = {}, include = []) {
    return function ({addBase, theme}) {
        let customProps = {};

        for (const [namedColor, shades] of Object.entries(namedColors)) {
            for (const [shade, hex] of Object.entries(shades)) {
                const channels = hexToRgb(hex);
                customProps[`--${namedColor}-${shade}`] = channels.join(' ');
            }
        }

        if (hasElements(include)) {
            const themePalettes = theme('colors');

            for (const [color, shades] of Object.entries(themePalettes)) {
                if (!include.includes(color)) continue;

                for (const [shade, hex] of Object.entries(shades)) {
                    const channels = hexToRgb(hex);
                    customProps[`--${color}-${shade}`] = channels.join(' ');
                }
            }
        }

        addBase({
            html: customProps
        });
    };
}

/**
 * Creates color tokens that point to the relative CSS custom properties,
 * like the ones created by `addColorsToBaseAsCSSProps`
 * @see {@link addColorsToBaseAsCSSProps}
 */
export function addNamedColorsTokens(shades, colorName, fallback) {
    let tokens = {};
    for (const shade of shades) {
        const cssPropName = `${colorName}-${shade}`;
        let value = `rgb(var(--${cssPropName}) / <alpha-value>)`;
        if (fallback) {
            const channels = hexToRgb(fallback[shade]).join(' ');
            value = `rgb(var(--${cssPropName}, ${channels}) / <alpha-value>)`;
        }
        tokens[shade] = value;
    }
    return {[colorName]: tokens};
}
/**
 * Given a primary color from Tailwind preset, match it
 * with neutral color with the right color temperature
 * @param {string} primary
 * @returns {string}
 */
export function associateNeutralToPrimary(primary) {
    switch (primary) {
        case 'red':
        case 'orange':
        case 'amber':
        case 'rose':
            return 'stone';
        case 'yellow':
        case 'lime':
        case 'fuchsia':
        case 'pink':
            return 'neutral';
        case 'green':
        case 'emerald':
        case 'purple':
            return 'zinc';
        case 'teal':
        case 'cyan':
        case 'violet':
            return 'gray';
        case 'sky':
        case 'blue':
        case 'indigo':
            return 'slate';
        default:
            return 'gray';
    }
}

/**
 * Given a color keyword within the default color palettes, it sets its shades
 * as CSS custom properties at the document level
 * @param {string} namedColor "primary", "neutral", etc.
 * @param {string} keyword color keyword within Tailwind base colors
 * @param {number | string} shade 50, 100, 200, etc.
 */
export function setColorTokensFromKeyword(shades, namedColor, keyword) {
    for (const shade of shades) {
        const customProperty = `--${namedColor}-${shade}`;
        const value = `var(--${keyword}-${shade})`;
        document.documentElement.style.setProperty(customProperty, value);
    }
}

/**
 * Checks wether the passed object is a palette
 * with the right amount of shades
 * @param {object} palette
 * @returns
 */
export function isValidPalette(shades, palette) {
    try {
        return arrayEquals(Object.keys(palette), shades);
    } catch (error) {
        console.error(error);
        return false;
    }
}

/**
 * Adds CSS custom properties to the document for each
 * color in the palette
 */
export function setDocumentColorPalette(paletteName, palette) {
    for (const [shade, value] of Object.entries(palette)) {
        const customProperty = `--${paletteName}-${shade}`;
        document.documentElement.style.setProperty(customProperty, value);
    }
}
/**
 * Given a primary color from the preset, associate a neutral color with the right color temperature
 * @param {string} primary
 * @returns {string}
 */
export function associateNeutralToPrimaryPalette(primary) {
    switch (primary) {
        case 'red':
        case 'orange':
        case 'amber':
        case 'rose':
            return 'stone';
        case 'yellow':
        case 'lime':
        case 'fuchsia':
        case 'pink':
            return 'neutral';
        case 'green':
        case 'emerald':
        case 'purple':
            return 'zinc';
        case 'teal':
        case 'cyan':
        case 'violet':
            return 'gray';
        case 'sky':
        case 'blue':
        case 'indigo':
            return 'slate';
        default:
            return 'gray';
    }
}

/**
 * Given a valid palette, it sets its values as CSS custom properties
 * under the passed named color at the document level
 */
export function setColorTokensFromPalette(namedColor, palette) {
    for (const [shade, value] of Object.entries(palette)) {
        const customProperty = `--${namedColor}-${shade}`;
        document.documentElement.style.setProperty(customProperty, value);
    }
}
